import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { UsuarioContext } from '../../providers/personaContext';
import { ShoppingCart, Settings } from 'lucide-react';
import { getIcon, ModuleIcons } from './icons';
import { isSuperAdmin } from './auth';
import CustomTooltip from '../../elementos/eventos/avisos/Tooltip';
import { useTiposNavegables } from './hooks/useTiposNavegables';
import { agruparPorCategoria } from './utils/agruparTipos';
import { rutaTipo } from './utils/rutaTipo';

/**
 * Submenú del módulo Inventario, diseñado para vivir DENTRO del
 * `<ul className="lista lista-secundaria">` de Sidebar.js (la franja
 * gris clara entre el rail oscuro de iconos y el contenido).
 *
 * INTEGRACIÓN:
 *   En Sidebar.js, dentro del if-ladder de `numbtn`, se monta:
 *     {numbtn === 9 && <ul><InventarioSidebar showSidebar={showSidebar} /></ul>}
 *
 * PATRÓN VISUAL:
 *   Replica el patrón de los demás MenuXXX de SubSiderbar.js: cada item
 *   es un <li> con un <NavLink> que envuelve un <div className="menuBtn">.
 *   En modo collapsed (showSidebar=false), solo se ven los iconos.
 *
 *   Añadidos propios del módulo:
 *     - Dos cabeceras de sección no clickeables ("Navegación", "Catálogo")
 *       que se ocultan en modo collapsed.
 *     - Lista dinámica de tipos cargada del backend.
 *     - Badge con número de unidades por tipo.
 *     - "Permisos" oculto si el usuario no es Super Administrador.
 */

const SECTION_HEADER_STYLE = {
  padding: '14px 12px 4px',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#64748b',          // slate-500
  listStyle: 'none',
  pointerEvents: 'none',
  userSelect: 'none',
};

const BADGE_STYLE = {
  marginLeft: 'auto',
  fontSize: '11px',
  color: '#94a3b8',          // slate-400
  fontVariantNumeric: 'tabular-nums',
  paddingLeft: '8px',
};

// Sub-cabecera de categoría: tercer nivel dentro de cada scope. Más sutil
// que la cabecera de scope y un poco indentada para marcar la jerarquía.
const SUBCAT_HEADER_STYLE = {
  ...SECTION_HEADER_STYLE,
  fontSize: '10px',
  opacity: 0.75,
  paddingLeft: '24px',
};


function MenuItem({ to, end, Icon, label, badge, showSidebar }) {
  return (
    <li>
      <NavLink activeclassname="active" end={end} to={to}>
        <div className={`menuBtn ${showSidebar ? 'expanded' : 'collapsed'}`}>
          {showSidebar ? (
            <>
              <Icon />
              <span className="menuText">{label}</span>
              {badge !== undefined && badge !== null && (
                <span style={BADGE_STYLE}>{badge}</span>
              )}
            </>
          ) : (
            <CustomTooltip mensaje={label} alineado="left" tamano="1.0rem">
              <div>
                <Icon />
              </div>
            </CustomTooltip>
          )}
        </div>
      </NavLink>
    </li>
  );
}


export default function InventarioSidebar({ showSidebar = true }) {
  // Tipos NAVEGABLES (Capa 2): ya vienen filtrados por can_view del usuario.
  const { tipos, loading } = useTiposNavegables();
  // Permisos finos del usuario (los carga el proyecto en UsuarioContext,
  // vía GET /api/permisos/mios). Mismo origen que usa el Sidebar principal.
  const ctx = useContext(UsuarioContext) || {};
  const permisos = Array.isArray(ctx.permisos) ? ctx.permisos : [];
  const superAdmin = isSuperAdmin();
  // Mientras no lleguen los permisos, no pintamos entradas (evita el
  // parpadeo "todas → filtradas"). Super Admin no espera (va por el JWT).
  const permisosCargados = superAdmin || ctx.permisosCargados !== false;
  // Helper de permiso: Super Admin ve todo; el resto, según su lista.
  const can = (clave) => superAdmin || permisos.includes(clave);
  const showPermissions = superAdmin;

  // ¿Mostrar la cabecera "Navegación"? Solo si hay alguna entrada visible.
  const hayNavegacion =
    can('inventario.dashboard.view') ||
    can('inventario.tipos.view') ||
    can('inventario.stock-entries.view') ||
    can('inventario.eventos.view') ||
    showPermissions;

  // Hasta que lleguen los permisos no pintamos entradas: así no aparece
  // el subsidebar completo durante un instante y luego se filtra.
  if (!permisosCargados) {
    return null;
  }

  return (
    <>
      {showSidebar && hayNavegacion && <li style={SECTION_HEADER_STYLE}>Navegación</li>}

      {can('inventario.dashboard.view') && (
        <MenuItem
          to="/inventario"
          end
          Icon={ModuleIcons.Dashboard}
          label="Dashboard"
          showSidebar={showSidebar}
        />
      )}
      {can('inventario.tipos.view') && (
        <MenuItem
          to="/inventario/tipos"
          Icon={ModuleIcons.Catalog}
          label="Tipos"
          showSidebar={showSidebar}
        />
      )}
      {can('inventario.stock-entries.view') && (
        <MenuItem
          to="/inventario/compras"
          Icon={ShoppingCart}
          label="Compras"
          showSidebar={showSidebar}
        />
      )}
      {can('inventario.eventos.view') && (
        <MenuItem
          to="/inventario/historial"
          Icon={ModuleIcons.History}
          label="Historial"
          showSidebar={showSidebar}
        />
      )}
      {showPermissions && (
        <MenuItem
          to="/inventario/ajustes"
          Icon={Settings}
          label="Ajustes"
          showSidebar={showSidebar}
        />
      )}

      {!loading && (() => {
        // Los tipos vienen del backend YA filtrados por can_view (Capa 2):
        // son las "puertas" a los activos que este usuario puede ver. Los
        // partimos en dos secciones (interno / colaboradores) y, dentro de
        // cada una, los agrupamos por categoría (Pieza 3). Si en un scope
        // ningún tipo tiene categoría, se pinta plano como antes.
        // En modo collapsed se ocultan cabeceras y sub-cabeceras.
        const internos = tipos.filter((t) => t.scope !== 'colab');
        const colab = tipos.filter((t) => t.scope === 'colab');

        const renderTipo = (t, prefix) => (
          <MenuItem
            key={`${prefix}-${t.slug}`}
            // El ámbito va en la ruta: los dos gemelos de un slug tienen
            // destinos distintos y NavLink deja de resaltar los dos.
            to={rutaTipo(t.scope, t.slug)}
            Icon={getIcon(t.icon)}
            label={t.nombre}
            badge={typeof t.unidades === 'number' ? t.unidades : null}
            showSidebar={showSidebar}
          />
        );

        const renderScope = (items, prefix) => {
          const { grupos, sinCat } = agruparPorCategoria(items);
          if (grupos.length === 0) {
            return items.map((t) => renderTipo(t, prefix));
          }
          return (
            <>
              {grupos.map((g) => (
                <React.Fragment key={`${prefix}-cat-${g.cat.id}`}>
                  {showSidebar && (
                    <li style={SUBCAT_HEADER_STYLE}>{g.cat.nombre}</li>
                  )}
                  {g.tipos.map((t) => renderTipo(t, prefix))}
                </React.Fragment>
              ))}
              {sinCat.length > 0 && (
                <>
                  {showSidebar && (
                    <li style={SUBCAT_HEADER_STYLE}>Sin categoría</li>
                  )}
                  {sinCat.map((t) => renderTipo(t, prefix))}
                </>
              )}
            </>
          );
        };

        return (
          <>
            {internos.length > 0 && (
              <>
                {showSidebar && (
                  <li style={SECTION_HEADER_STYLE}>Interno · GRUPOLAE</li>
                )}
                {renderScope(internos, 'interno')}
              </>
            )}
            {colab.length > 0 && (
              <>
                {showSidebar && (
                  <li style={SECTION_HEADER_STYLE}>Colaboradores</li>
                )}
                {renderScope(colab, 'colab')}
              </>
            )}
          </>
        );
      })()}
    </>
  );
}
